import axios from "axios";

import { twoDriveApi } from "./api";
import type { DriveNode } from "./types";

/**
 * Upload one file end-to-end, transparently handling all three backend modes:
 *   proxy         → POST through the backend (dev / local storage)
 *   s3-put        → single presigned PUT straight to S3
 *   s3-multipart  → presigned per-part PUTs to S3, then complete
 *
 * S3 PUTs use a bare axios instance so the app's Authorization interceptor never
 * touches the presigned request (which would break the signature). Returns the
 * finalized, ACTIVE DriveNode. On failure the upload session is aborted.
 */
export async function uploadFile(
  file: File,
  parent: string | null,
  onProgress?: (pct: number) => void,
): Promise<DriveNode> {
  const contentType = file.type || "application/octet-stream";
  const plan = await twoDriveApi.initiateUpload({
    name: file.name,
    parent,
    size_bytes: file.size,
    content_type: contentType,
  });

  try {
    if (plan.mode === "proxy") {
      await twoDriveApi.localUpload(plan.node_id, file, onProgress);
      return await twoDriveApi.completeUpload(plan.node_id, {});
    }

    if (plan.mode === "s3-put") {
      await axios.put(plan.url, file, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total ?? file.size))),
      });
      return await twoDriveApi.completeUpload(plan.node_id, {});
    }

    // s3-multipart
    const parts: { part_number: number; etag: string }[] = [];
    let uploaded = 0;
    for (const part of plan.parts) {
      const start = (part.part_number - 1) * plan.part_size;
      const blob = file.slice(start, start + plan.part_size);
      const res = await axios.put(part.url, blob, {
        onUploadProgress: (e) =>
          onProgress?.(Math.round(((uploaded + e.loaded) * 100) / file.size)),
      });
      const headers = res.headers as Record<string, string>;
      const etag = headers.etag ?? headers.ETag;
      parts.push({ part_number: part.part_number, etag });
      uploaded += blob.size;
    }
    return await twoDriveApi.completeUpload(plan.node_id, { upload_id: plan.upload_id, parts });
  } catch (err) {
    const uploadId = "upload_id" in plan ? plan.upload_id : undefined;
    twoDriveApi.abortUpload(plan.node_id, uploadId).catch(() => {});
    throw err;
  }
}

/** A file queued for upload plus its directory path relative to the destination folder. */
export interface PendingUpload {
  file: File;
  /** Directory segments relative to the destination (empty = directly inside it). */
  dir: string[];
}

/** Directory tree gathered from a folder picker or a drag-and-drop. */
export interface UploadTree {
  uploads: PendingUpload[];
  /** Every directory seen, including empty ones, so the structure is replicated exactly. */
  dirs: string[][];
}

/**
 * Build an UploadTree from an `<input type="file">` selection. Files picked via a
 * folder input (webkitdirectory) carry their position in `webkitRelativePath`;
 * plain file picks have it empty and land directly in the destination.
 */
export function treeFromFileList(files: FileList | File[]): UploadTree {
  const uploads = Array.from(files).map((file) => ({
    file,
    dir: file.webkitRelativePath ? file.webkitRelativePath.split("/").slice(0, -1) : [],
  }));
  const dirs = new Map<string, string[]>();
  for (const u of uploads) {
    for (let i = 1; i <= u.dir.length; i++) {
      const prefix = u.dir.slice(0, i);
      dirs.set(prefix.join("/"), prefix);
    }
  }
  return { uploads, dirs: [...dirs.values()] };
}

/**
 * Build an UploadTree from a drop, recursing into dropped directories via the
 * FileSystem entry API. Entries must be captured synchronously — the DataTransfer
 * item list is gone once the drop handler yields — so call this before any await.
 * Falls back to the flat file list where the entry API is unavailable.
 */
export function treeFromDataTransfer(dt: DataTransfer): Promise<UploadTree> {
  const entries = Array.from(dt.items)
    .filter((item) => item.kind === "file")
    .map((item) => item.webkitGetAsEntry?.() ?? null);
  if (!entries.some(Boolean)) return Promise.resolve(treeFromFileList(dt.files));

  const tree: UploadTree = { uploads: [], dirs: [] };

  async function walk(entry: FileSystemEntry, dir: string[]): Promise<void> {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject),
      );
      tree.uploads.push({ file, dir });
    } else if (entry.isDirectory) {
      const next = [...dir, entry.name];
      tree.dirs.push(next);
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns batches (Chrome caps them at 100) — drain until empty.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
          reader.readEntries(resolve, reject),
        );
        if (!batch.length) break;
        for (const child of batch) await walk(child, next);
      }
    }
  }

  return (async () => {
    for (const entry of entries) if (entry) await walk(entry, []);
    return tree;
  })();
}

/**
 * Resolve relative directory paths to folder node ids, creating missing folders
 * on demand. Results (and failures) are cached per path so each directory is
 * ensured at most once per upload session.
 */
export function createDirResolver(root: string | null): (dir: string[]) => Promise<string | null> {
  const cache = new Map<string, Promise<string | null>>();
  return (dir) => {
    if (dir.length === 0) return Promise.resolve(root);
    const key = dir.join("/");
    let hit = cache.get(key);
    if (!hit) {
      hit = twoDriveApi.ensureFolderPath(root, dir).then((node) => node.id);
      cache.set(key, hit);
    }
    return hit;
  };
}
